import {ChangeDetectionStrategy, Component} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {AuthService} from "../_services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {SnackbarService} from "../_services/snackbar.service";

@Component({
    selector: 'app-accept-invite',
    templateUrl: './accept-invite.component.html',
    styleUrl: './accept-invite.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AcceptInviteComponent {
    councilLogo: string | undefined;
    authorityName: string | undefined;
    loading: boolean = false;
    user: any;
    isSsoUser: boolean = false;
    passwordForm: FormGroup;
    token: string | null = null;
    uid: string | null = null;
    passwordLength: number = 8;

    constructor(private readonly formBuilder: FormBuilder,
                private authService: AuthService,
                private route: ActivatedRoute,
                private readonly router: Router,
                private snackbarService: SnackbarService) {
        localStorage.clear();
        this.route.params.subscribe({
            next: params => {
                this.uid = params['uid'];
                this.token = params['token'];
                if (this.uid && this.token) {
                    this.authService.checkUserInvite(this.uid, this.token).subscribe({
                        next: r => {
                            this.user = r.user;
                            if (r.user?.is_sso_user) {
                                this.isSsoUser = true;
                            }
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            if (error.toLowerCase().includes('account is already activated')) {
                                this.router.navigate(['/login'])
                            }
                        }
                    });
                } else {
                    this.loading = false;
                    this.snackbarService.openSnackBar('Token and UUID not available please try again', 'error')
                }
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error);
            }
        });

        this.passwordForm = this.formBuilder.group({
            password: ['', this.passwordValidators(8)],
            confirmPassword: ['', this.passwordValidators(8)],
        }, {validators: this.passwordMatchValidator})
        this.authService.getDynamicSettings().then((r): void => {
            this.councilLogo = r.authority_logo;
            this.authorityName = r.authority_name;
            if (r.minimum_password_length) {
                this.passwordLength = r.minimum_password_length;
                this.passwordForm.controls['password'].setValidators(this.passwordValidators(this.passwordLength));
                this.passwordForm.controls['confirmPassword'].setValidators(this.passwordValidators(this.passwordLength));
                this.passwordForm.updateValueAndValidity();
            }
        });
    }

    resetPassword(): void {
        this.loading = true;
        let body = {
            password: this.passwordForm.value.password,
            confirm_password: this.passwordForm.value.confirmPassword
        }
        if (this.uid && this.token) {
            this.authService.acceptUserInvite(this.uid, this.token, body).subscribe({
                next: r => {
                    this.loading = false;
                    this.snackbarService.openSnackBar('Password set successful. Please Log in.', 'success');
                    this.router.navigate(['/login'], {queryParams: {username: this.user.username}});
                }, error: error => {
                    this.loading = false;
                    this.snackbarService.openSnackBar(error, 'error')
                }
            });
        } else {
            this.loading = false;
            this.snackbarService.openSnackBar('Token and UUID not available please try again', 'error')
        }
    }

    private passwordValidators(length: number): ValidatorFn[] {
        const specialCharacters: string = `[!@#$%^&*()_+\\-=\\[\\]{};':"\\\\|,.<>\\/?]`;
        const pattern: string = `^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*${specialCharacters})(?!.*\\s).{${length},}$`;

        return [Validators.required, Validators.minLength(length), Validators.pattern(pattern)];
    }

    private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
        const password: string | null = group.get('password')?.value;
        const confirmPassword: string | null = group.get('confirmPassword')?.value;

        return password === confirmPassword ? null : {passwordMismatch: true};
    }
}
